// src/pages/materias/laboral/LaboralProcedimientoPage/materias/laboral/useTribunalProcedimiento.js

import { useEffect, useMemo } from "react";
import dayjs from "dayjs";

const _isWeekend = (d) => { const day = d.day(); return day === 0 || day === 6; };

const _addBusinessDays = (start, businessDays) => {
  if (!start || !Number.isFinite(Number(businessDays))) return null;
  let d = dayjs(start).startOf("day");
  let remaining = Number(businessDays);
  while (remaining > 0) { d = d.add(1, "day"); if (!_isWeekend(d)) remaining -= 1; }
  return d;
};

const _businessDaysLeft = (from, to) => {
  if (!from || !to) return null;
  const a = dayjs(from).startOf("day");
  const b = dayjs(to).startOf("day");
  if (b.isSame(a, "day") || b.isBefore(a, "day")) return 0;
  let count = 0;
  let d = a.add(1, "day");
  while (d.isBefore(b, "day") || d.isSame(b, "day")) { if (!_isWeekend(d)) count += 1; d = d.add(1, "day"); }
  return count;
};

const _calcTerminosVigentes = (exp, today) => {
  const pares = [
    [exp?.etapa4_fecha_notif, exp?.etapa4_dias_habiles],
    [exp?.etapa5_fecha_notif, exp?.etapa5_dias_habiles],
    [exp?.etapa6_fecha_notif, exp?.etapa6_dias_habiles],
    [exp?.etapa7_fecha_notif, exp?.etapa7_dias_habiles],
    [exp?.etapa8_fecha_notif, exp?.etapa8_dias_habiles],
    [exp?.etapa9_fecha_notif, exp?.etapa9_dias_habiles],
  ];
  let count = 0;
  pares.forEach(([fecha, dias]) => {
    if (!fecha || dias == null) return;
    const deadline = _addBusinessDays(fecha, Number(dias));
    if (!deadline) return;
    const diasLeft = _businessDaysLeft(today, deadline);
    if (diasLeft !== null && diasLeft > 0) count += 1;
  });
  // También contar fecha_limite_contestacion cuando no hay términos de etapas
  if (count === 0 && exp?.fecha_limite_contestacion) {
    const lim = dayjs(exp.fecha_limite_contestacion).startOf("day");
    if (lim.isValid() && (lim.isSame(today) || lim.isAfter(today))) count += 1;
  }
  // Término capturado en la bitácora de Actuaciones (tribunal_actuaciones) —
  // fuente independiente de las etapas viejas, así que se suma aparte.
  if (_actuacionTerminoVigente(exp, today)) count += 1;
  return count;
};

// Término vigente más próximo capturado en el tab "Actuaciones" (reemplazó a
// tribunal_etapas para registrar términos nuevos desde ahí).
const _actuacionTerminoVigente = (exp, today) => {
  if (!exp?.actuacion_termino_fecha_limite) return false;
  const lim = dayjs(exp.actuacion_termino_fecha_limite).startOf("day");
  return lim.isValid() && (lim.isSame(today) || lim.isAfter(today));
};

const _terminoVigenteEtapa = (exp, etapaNum, today) => {
  const fecha = exp[`etapa${etapaNum}_fecha_notif`];
  const dias = exp[`etapa${etapaNum}_dias_habiles`];
  if (!fecha || dias == null) return false;
  const deadline = _addBusinessDays(fecha, Number(dias));
  if (!deadline) return false;
  const diasLeft = _businessDaysLeft(today, deadline);
  return diasLeft !== null && diasLeft > 0;
};

const FORMAS_TERMINACION_BY_ID = {
  1: "Convenio",
  2: "Desistimiento",
  3: "Sentencia / Laudo favorable",
  4: "Sentencia / Laudo desfavorable",
  5: "Caducidad",
  6: "Sobreseimiento",
  7: "Incompetencia",
  8: "Acumulación a otro expediente",
};

const _resolveFormaName = (exp) => {
  const nombre = (exp.forma_terminacion_nombre || "").trim();
  // If nombre is a numeric string (id fallback from SQL COALESCE) or empty, look up by id
  if (!nombre || /^\d+$/.test(nombre)) {
    const id = Number(exp.id_forma_terminacion);
    return FORMAS_TERMINACION_BY_ID[id] || (nombre || "Sin clasificar");
  }
  return nombre;
};

export default function useTribunalProcedimiento({
  concSlice,
  search,
  statusFilter,
  audienciaSubFilter = "todos",   // "todos" | "etapa10" | "etapa11" | "etapa12"
  terminoSubFilter = "todos",     // "todos" | "etapa4" | ... | "etapa9"
  concluidosSubFilter = "todos",  // "todos" | nombre de la forma de terminación
  dateRange,
  sortOption = "default",
  currentPage,
  pageSize,
  setCurrentPage,
}) {
  // "Pendiente de información" es un AVISO independiente (no una etapa): todo
  // expediente creado por carga rápida se muestra aquí, sin importar en qué
  // etapa vaya. No se condiciona a si ya tiene citatorio u otros datos.
  const _isPendienteInfo = (exp) =>
    exp?.carga_rapida === 1 || exp?.carga_rapida === true || exp?.carga_rapida === "1";
  const expedientesItems = useMemo(() => {
    const raw = concSlice?.data ?? concSlice;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.items)) return raw.items;
    if (raw?.items && Array.isArray(raw.items.items)) return raw.items.items;
    return [];
  }, [concSlice]);

  const searchedExpedientes = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();
    if (!term) return expedientesItems;

    return expedientesItems.filter((exp) => {
      const razonesSociales = Array.isArray(exp?.razones_sociales)
        ? exp.razones_sociales.map((r) => r?.razon_social).filter(Boolean).join(" ")
        : "";

      const campos = [
        exp.expediente, exp.numero_expediente, exp.expediente_anterior,
        exp.corresponsal, exp.nombre_trabajador, exp.nombre_parte_actora,
        exp.accion_intentada, exp.nombre_autoridad, exp.nombre_ciudad,
        exp.nombre_estado, exp.empresa_nombre, razonesSociales, exp.id, exp.code,
      ];
      return campos.some((v) => v && String(v).toLowerCase().includes(term));
    });
  }, [search, expedientesItems]);

  const baseForCounts = useMemo(() => {
    let list = [...searchedExpedientes];
    if (dateRange && (dateRange[0] || dateRange[1])) {
      const start = dateRange[0] ? dayjs(dateRange[0]) : null;
      const end = dateRange[1] ? dayjs(dateRange[1]) : null;
      list = list.filter((exp) => {
        const candidates = [
          exp.fecha_recepcion_demanda, exp.fecha_limite_contestacion,
          exp.fecha_audiencia_conciliadora, exp.datetime,
        ].filter(Boolean);
        if (!candidates.length) return false;
        return candidates.some((fechaRaw) => {
          const d = dayjs(fechaRaw);
          if (!d.isValid()) return false;
          const afterStart = !start || d.isSame(start, "day") || d.isAfter(start, "day");
          const beforeEnd = !end || d.isSame(end, "day") || d.isBefore(end, "day");
          return afterStart && beforeEnd;
        });
      });
    }
    return list;
  }, [searchedExpedientes, dateRange]);

  const statusCounts = useMemo(() => {
    const base = {
      proximasAudiencias: 0,
      proximasEtapa10: 0,
      proximasEtapa11: 0,
      proximasEtapa12: 0,
      terminosCriticos: 0,
      terminosEtapa4: 0,
      terminosEtapa5: 0,
      terminosEtapa6: 0,
      terminosEtapa7: 0,
      terminosEtapa8: 0,
      terminosEtapa9: 0,
      pendientesNotificar: 0,
      pendientesInformacion: 0,
      sinActividad15d: 0,
      concluidos: 0,
      concluidosByForma: {},
      vencidos: 0,
      activos: 0,
    };

    const today = dayjs().startOf("day");

    const _esFutura = (f) => {
      const d = dayjs(f).startOf("day");
      return d.isValid() && (d.isSame(today) || d.isAfter(today));
    };

    baseForCounts.forEach((exp) => {
      const active = !(exp?.active === 0 || exp?.active === "0");

      // Card: Concluidos — independiente de active (incluye soft-deleted con terminacion)
      if (exp?.id_forma_terminacion) {
        base.concluidos += 1;
        const forma = _resolveFormaName(exp);
        base.concluidosByForma[forma] = (base.concluidosByForma[forma] || 0) + 1;
      }

      if (!active) return;

      // Un expediente concluido ya no cuenta en ningún bucket de "etapa activa"
      // (Próximas Audiencias, Términos Críticos, Pendientes, etc.) — solo en Concluidos.
      if (exp?.id_forma_terminacion) return;

      // Card: Próximas Audiencias — un expediente solo cuenta UNA vez, en la etapa
      // de audiencia más avanzada que tenga capturada (10 < 11 < 12), igual que el
      // badge de la tarjeta. Así "Todos" siempre coincide con la suma de las etapas.
      const aud10 = !!exp?.fecha_audiencia_conciliadora;
      const aud11 = !!exp?.fecha_audiencia_etapa11;
      const aud12 = !!exp?.fecha_audiencia_etapa12;
      if (aud12) { base.proximasAudiencias += 1; base.proximasEtapa12 += 1; }
      else if (aud11) { base.proximasAudiencias += 1; base.proximasEtapa11 += 1; }
      else if (aud10) { base.proximasAudiencias += 1; base.proximasEtapa10 += 1; }

      // Card: Términos Críticos — +1 por cada término vigente en etapas 4-9, también por etapa
      const etapaPares = [
        [4, exp?.etapa4_fecha_notif, exp?.etapa4_dias_habiles],
        [5, exp?.etapa5_fecha_notif, exp?.etapa5_dias_habiles],
        [6, exp?.etapa6_fecha_notif, exp?.etapa6_dias_habiles],
        [7, exp?.etapa7_fecha_notif, exp?.etapa7_dias_habiles],
        [8, exp?.etapa8_fecha_notif, exp?.etapa8_dias_habiles],
        [9, exp?.etapa9_fecha_notif, exp?.etapa9_dias_habiles],
      ];
      let terminosVigentes = 0;
      etapaPares.forEach(([num, fecha, dias]) => {
        if (!fecha || dias == null) return;
        const deadline = _addBusinessDays(fecha, Number(dias));
        if (!deadline) return;
        const diasLeft = _businessDaysLeft(today, deadline);
        if (diasLeft !== null && diasLeft > 0) {
          terminosVigentes += 1;
          base[`terminosEtapa${num}`] += 1;
        }
      });
      // También contar fecha_limite_contestacion cuando no hay términos de etapas
      if (terminosVigentes === 0 && exp?.fecha_limite_contestacion && _esFutura(exp.fecha_limite_contestacion)) {
        terminosVigentes += 1;
      }
      // Término capturado en la bitácora de Actuaciones — fuente independiente
      // de las etapas viejas (no tiene un número de etapa fijo, así que solo
      // suma al total, no a un sub-filtro etapaN).
      if (_actuacionTerminoVigente(exp, today)) {
        terminosVigentes += 1;
      }
      base.terminosCriticos += terminosVigentes;

      // Card: Activos — unión de Términos Críticos y Próximas Audiencias, un
      // expediente cuenta una sola vez aunque esté en ambos buckets.
      const tieneAudiencia = aud10 || aud11 || aud12;
      if (tieneAudiencia || terminosVigentes > 0) base.activos += 1;

      // Card: Pendientes de Notificar — solo sin audiencias, sin términos y sin terminación
      if (!tieneAudiencia && terminosVigentes === 0 && !exp?.id_forma_terminacion) {
        const doc = exp?.id_tribunal_documento_citatorio;
        const hasCitatorio = doc !== null && doc !== undefined && Number(doc) !== 0;
        if (!hasCitatorio) { base.pendientesNotificar += 1; }
        else { base.sinActividad15d += 1; }
      }

      // Vencidos: sin citatorio (SIN DEMANDA)
      const docV = exp?.id_tribunal_documento_citatorio;
      const hasCitatorioV = docV !== null && docV !== undefined && Number(docV) !== 0;
      if (!hasCitatorioV && !exp?.id_forma_terminacion) base.vencidos += 1;

      // Pendientes de información: creados mediante Carga Rápida
      if (_isPendienteInfo(exp)) base.pendientesInformacion += 1;
    });

    return base;
  }, [baseForCounts]);

  const filteredExpedientes = useMemo(() => {
    let list = [...searchedExpedientes];

    if (statusFilter && statusFilter !== "todos") {
      const today = dayjs().startOf("day");

      const _esFutura = (f) => {
        const d = dayjs(f).startOf("day");
        return d.isValid() && (d.isSame(today) || d.isAfter(today));
      };

      // No se exige que la fecha sea futura: si el expediente ya está en etapa
      // 10/11/12 (tiene la fecha capturada), cuenta como "Próximas Audiencias".
      const _tieneAudienciaVigente = (exp) => {
        return !!exp.fecha_audiencia_conciliadora ||
               !!exp.fecha_audiencia_etapa11 ||
               !!exp.fecha_audiencia_etapa12;
      };

      const _tieneTerminoCritico = (exp) => _calcTerminosVigentes(exp, today) > 0;

      const _hasCitatorio = (exp) => {
        const doc = exp.id_tribunal_documento_citatorio;
        return doc !== null && doc !== undefined && Number(doc) !== 0;
      };

      list = list.filter((exp) => {
        // Concluidos incluye soft-deleted (active=0) que tienen forma de terminacion
        if (statusFilter === "concluidos") {
          if (!exp?.id_forma_terminacion) return false;
          if (concluidosSubFilter !== "todos") {
            return _resolveFormaName(exp) === concluidosSubFilter;
          }
          return true;
        }

        const active = !(exp?.active === 0 || exp?.active === "0");
        if (!active) return false;

        // Un expediente concluido ya no pertenece a ningún filtro de "etapa activa".
        if (exp?.id_forma_terminacion) return false;

        if (statusFilter === "activos") {
          return _tieneAudienciaVigente(exp) || _tieneTerminoCritico(exp);
        }

        if (statusFilter === "conLimite") {
          if (terminoSubFilter !== "todos") {
            const num = terminoSubFilter.replace("etapa", "");
            return _terminoVigenteEtapa(exp, num, today);
          }
          return _tieneTerminoCritico(exp);
        }

        if (statusFilter === "conAudiencia") {
          // Igual que en el conteo: la etapa más avanzada gana, para que cada
          // expediente caiga en un solo sub-filtro (10, 11 o 12), nunca en varios.
          if (audienciaSubFilter === "etapa12") return !!exp.fecha_audiencia_etapa12;
          if (audienciaSubFilter === "etapa11") return !!exp.fecha_audiencia_etapa11 && !exp.fecha_audiencia_etapa12;
          if (audienciaSubFilter === "etapa10") return !!exp.fecha_audiencia_conciliadora && !exp.fecha_audiencia_etapa11 && !exp.fecha_audiencia_etapa12;
          return _tieneAudienciaVigente(exp);
        }

        if (statusFilter === "sinCitatorio") {
          return !_tieneAudienciaVigente(exp) && !_tieneTerminoCritico(exp) &&
                 !exp?.id_forma_terminacion && !_hasCitatorio(exp);
        }

        if (statusFilter === "sinActividad15d") {
          return !_tieneAudienciaVigente(exp) && !_tieneTerminoCritico(exp) &&
                 !exp?.id_forma_terminacion && _hasCitatorio(exp);
        }

        if (statusFilter === "vencidos") {
          return !_hasCitatorio(exp) && !exp?.id_forma_terminacion;
        }

        if (statusFilter === "pendienteInformacion") {
          return _isPendienteInfo(exp);
        }

        return true;
      });
    }

    if (dateRange && (dateRange[0] || dateRange[1])) {
      const start = dateRange[0] ? dayjs(dateRange[0]) : null;
      const end = dateRange[1] ? dayjs(dateRange[1]) : null;
      list = list.filter((exp) => {
        const candidates = [
          exp.fecha_recepcion_demanda, exp.fecha_limite_contestacion,
          exp.fecha_audiencia_conciliadora, exp.datetime,
        ].filter(Boolean);
        if (!candidates.length) return false;
        return candidates.some((fechaRaw) => {
          const d = dayjs(fechaRaw);
          if (!d.isValid()) return false;
          const afterStart = !start || d.isSame(start, "day") || d.isAfter(start, "day");
          const beforeEnd = !end || d.isSame(end, "day") || d.isBefore(end, "day");
          return afterStart && beforeEnd;
        });
      });
    }

    return list;
  }, [searchedExpedientes, statusFilter, audienciaSubFilter, terminoSubFilter, concluidosSubFilter, dateRange]);

  const getCaseTitleSortKey = (exp) => {
    const actorName = exp?.nombre_trabajador || exp?.trabajador_nombre || exp?.actor || exp?.nombre_parte_actora || "";
    const razones = Array.isArray(exp?.razones_sociales)
      ? exp.razones_sociales.map((x) => (x?.razon_social || "").replace(/\s+/g, " ").trim()).filter(Boolean)
      : [];
    const contraName = razones.length > 0 ? razones.join(" / ") : (exp?.empresa_nombre || exp?.contraparte || "");
    return `${actorName} vs ${contraName}`.toLowerCase().trim();
  };

  const getAudienciaFecha = (exp) =>
    exp?.fecha_audiencia_etapa12 || exp?.fecha_audiencia_etapa11 || exp?.fecha_audiencia_conciliadora || null;
  const getSubidoFecha = (exp) => exp?.created_at || null;

  const getDateValue = (v) => {
    const d = dayjs(v);
    return d.isValid() ? d.valueOf() : null;
  };

  const compareByGetter = (getter, dir) => (a, b) => {
    const da = getDateValue(getter(a));
    const db = getDateValue(getter(b));
    if (da === null && db === null) return 0;
    if (da === null) return 1; // sin fecha -> al final
    if (db === null) return -1;
    return dir === "asc" ? da - db : db - da;
  };

  const sortedExpedientes = useMemo(() => {
    const list = [...filteredExpedientes];

    switch (sortOption) {
      case "az":
        list.sort((a, b) => getCaseTitleSortKey(a).localeCompare(getCaseTitleSortKey(b)));
        break;
      case "za":
        list.sort((a, b) => getCaseTitleSortKey(b).localeCompare(getCaseTitleSortKey(a)));
        break;
      case "audiencia_asc":
        list.sort(compareByGetter(getAudienciaFecha, "asc"));
        break;
      case "audiencia_desc":
        list.sort(compareByGetter(getAudienciaFecha, "desc"));
        break;
      case "subido_asc":
        list.sort(compareByGetter(getSubidoFecha, "asc"));
        break;
      case "subido_desc":
        list.sort(compareByGetter(getSubidoFecha, "desc"));
        break;
      default:
        // Sin ordenamiento elegido: siempre caer con los expedientes más
        // recientes primero (por fecha de creación, o id como respaldo).
        list.sort((a, b) => {
          const cmp = compareByGetter(getSubidoFecha, "desc")(a, b);
          if (cmp !== 0) return cmp;
          return Number(b?.id || 0) - Number(a?.id || 0);
        });
        break;
    }

    return list;
  }, [filteredExpedientes, sortOption]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, audienciaSubFilter, terminoSubFilter, concluidosSubFilter, dateRange, sortOption, setCurrentPage]);

  const paginatedExpedientes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedExpedientes.slice(start, start + pageSize);
  }, [sortedExpedientes, currentPage, pageSize]);

  return {
    statusCounts,
    filteredExpedientes,
    paginatedExpedientes,
    totalExp: filteredExpedientes.length,
  };
}
